import { formatMoney, postRequest } from "../../../Utils/Functions";
import { useUser } from "../../User/User.hook";
import { useEffect, useState, useRef } from "react";

const MAPFRE_SERVICE = "https://prod.infocasas.com.uy/microservices/simulador/?/mapfre/simulation";

export const useMapfreFinancing = ({ property_id, source = 0, propertyPrice = null }) => {
	const { user } = useUser();
	const amount = propertyPrice?.amount ?? 0;
	const currencyName = propertyPrice?.currency?.name ?? null;

	const [loading, setLoading] = useState(true);
	const [priceAmount, setPriceAmount] = useState(amount);
	const [currency, setCurrency] = useState(currencyName);
	const [minimumIncome, setMinimumIncome] = useState(0);
	const [annualFees, setAnnualFees] = useState(0);
	const [cash, setCash] = useState(0);
	const [extraData, setExtraData] = useState(null);
	const timerRef = useRef(null);
	const initialSimulationRef = useRef(false);

	const mapfreService = async (dontStore: boolean, value, currencyToUse = currency) => {
		setLoading(true);
		const userId =
			user && user.data && user.data.me && user.data.me.id ? user.data.me.id : "";

		let data = null;
		try {
			data = await postRequest(MAPFRE_SERVICE + (dontStore ? `/${dontStore}` : ""), {
				monto: value,
				IDpropiedad: property_id,
				IDusuario: userId,
				source: source,
			});
		} catch {
			return;
		}

		// postRequest devuelve el string "error" si falla el fetch
		if (!data || typeof data !== "object" || data.ingresos_necesarios == null) {
			return;
		}

		setPriceAmount(value);
		setMinimumIncome(data.ingresos_necesarios);
		setAnnualFees(data.costo_12_cuotas);
		setCash(data.costo_contado);
		setExtraData({
			importe_alquiler: formatMoney(value),
			ingresos_necesarios: `${currencyToUse} ${formatMoney(data.ingresos_necesarios)}`,
			costo_contado: `${currencyToUse} ${formatMoney(data.costo_contado)}`,
			costo_3_cuotas: `${currencyToUse} ${formatMoney(data.costo_3_cuotas)}`,
			costo_12_cuotas: `${currencyToUse} ${formatMoney(data.costo_12_cuotas)}`,
		});
		setLoading(false);
	};

	// El precio llega por props desde la ficha (pageProps.data), no desde el
	// cache de Apollo: la query property(id:) devuelve null en producción.
	useEffect(() => {
		if (!amount || !currencyName) return;

		setCurrency(currencyName);

		if (initialSimulationRef.current) return;
		initialSimulationRef.current = true;

		setPriceAmount(amount);
		mapfreService(true, amount, currencyName);
	}, [amount, currencyName]);

	const changePriceAmount = value => {
		// Reflejamos el valor tipeado al instante; el request va debounceado.
		// InputNumber emite null cuando se limpia el campo.
		setPriceAmount(typeof value === "number" ? value : 0);

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		timerRef.current = setTimeout(() => {
			if (value > 0) {
				mapfreService(false, value);
			}
		}, 1200);
	};

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	return {
		loading,
		currency,
		priceAmount: {
			value: priceAmount,
			change: changePriceAmount,
		},
		minimumIncome,
		annualFees,
		cash,
		extraData,
	};
};
